import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  Bot,
  CheckCircle2,
  CircleSlash,
  Info,
  KeyRound,
  Loader2,
  Lock,
  Pencil,
  Plus,
  RotateCcw,
  SearchX,
  Shield,
  ShieldAlert,
  UserX,
  Users,
  X,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/cn";
import { useAuthStore } from "@/store/useAuthStore";
import { useDrawerA11y } from "@/hooks/useDrawerA11y";
import {
  useAdminUsers,
  useSetUserActive,
  useSetUserLogin,
  useSetUserRoles,
  useSetUserTeam,
} from "@/hooks/useAdminUsers";
import { AddUserWizard } from "@/components/admin/AddUserWizard";
import { ManagerSyncSection } from "@/components/admin/ManagerSyncSection";
import { RoleAssignmentFields } from "@/components/admin/RoleAssignmentFields";
import type { AdminUserAllowedAction, AdminUserRow, RoleConfig } from "@/api/adminCenter";

/**
 * D-044 S1 — #1 Users list (VISUAL v1.1 LOCK).
 * Tier = resolver-derived + lock-маркер (AC-ADM-002) · warning-badges
 * (AC-ADM-004) · system приховані/read-only (P5/AC-ADM-005, MP4) ·
 * деактивація = confirm-dialog soft (MP5/AC-ADM-003) · «Змінити роль» =
 * canonical RoleAssignmentFields (MP3). Токени §2 — 0 raw palette.
 */

type TierFilter = "all" | "manager" | "supervisor" | "director" | "admin_data_all";
type StateFilter = "active" | "inactive" | "all";

export function AdminUsersPage() {
  const perms = useAuthStore((s) => s.permissions);
  const canView = !!perms?.canViewAdminCenter;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const listQ = useAdminUsers(canView);

  const [search, setSearch] = useState("");
  const [tierF, setTierF] = useState<TierFilter>("all");
  const [stateF, setStateF] = useState<StateFilter>("active");
  const [showSystem, setShowSystem] = useState(false);

  const [drawerUserId, setDrawerUserId] = useState<string | null>(null);
  const [wizardOpen, setWizardOpen] = useState(searchParams.get("new") === "1");
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);

  const showToast = (kind: "ok" | "err", msg: string) => {
    setToast({ kind, msg });
    window.setTimeout(() => setToast(null), 4000);
  };

  const filtered = useMemo(() => {
    const users = listQ.data?.users ?? [];
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (stateF !== "all" && u.isActive !== (stateF === "active")) return false;
      if (tierF !== "all" && u.tier !== tierF) return false;
      if (q && !`${u.name} ${u.email} ${u.userName}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [listQ.data, search, tierF, stateF]);

  const drawerUser = useMemo(
    () => listQ.data?.users.find((u) => u.id === drawerUserId) ?? null,
    [listQ.data, drawerUserId],
  );

  if (!canView) {
    return (
      <div className="p-4 md:p-6">
        <div className="mx-auto w-full max-w-[640px] rounded-lg border bg-muted/30 p-8 text-center">
          <ShieldAlert className="mx-auto mb-2 h-5 w-5 text-muted-foreground" />
          <p className="text-sm font-medium">Немає доступу до Адмін-центру</p>
          <p className="mt-1 text-xs text-muted-foreground">Розділ доступний лише адміністраторам.</p>
        </div>
      </div>
    );
  }

  const resetFilters = () => {
    setSearch("");
    setTierF("all");
    setStateF("active");
  };

  return (
    <div className="p-4 md:p-6">
      <div className="mx-auto w-full max-w-[1180px] space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
              <Users className="h-5 w-5" /> Користувачі
            </h1>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Доступ, команди та стан користувачів CRM.
            </p>
          </div>
          <Button size="sm" onClick={() => setWizardOpen(true)}>
            <Plus className="mr-1 h-4 w-4" /> Додати користувача
          </Button>
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Пошук (імʼя, email)…"
            className="h-9 w-56"
            aria-label="Пошук користувачів"
          />
          <select
            value={tierF}
            onChange={(e) => setTierF(e.target.value as TierFilter)}
            aria-label="Фільтр за рівнем доступу"
            className="h-9 rounded-lg border bg-card px-2 text-sm"
          >
            <option value="all">Рівень: усі</option>
            <option value="manager">Менеджер</option>
            <option value="supervisor">Керівник</option>
            <option value="director">Директор</option>
            <option value="admin_data_all">Адмін</option>
          </select>
          <select
            value={stateF}
            onChange={(e) => setStateF(e.target.value as StateFilter)}
            aria-label="Фільтр за станом"
            className="h-9 rounded-lg border bg-card px-2 text-sm"
          >
            <option value="active">Стан: активні</option>
            <option value="inactive">Деактивовані</option>
            <option value="all">Усі</option>
          </select>
          <label className="ml-auto flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={showSystem}
              onChange={(e) => setShowSystem(e.target.checked)}
              className="h-3.5 w-3.5"
            />
            Системні
          </label>
        </div>

        {/* Table */}
        <div className="rounded-card border bg-card">
          {listQ.isLoading && (
            <div className="space-y-2 p-4">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          )}
          {listQ.isError && (
            <div className="p-4 text-sm text-destructive">Не вдалося завантажити користувачів.</div>
          )}
          {listQ.data && (
            <>
              {filtered.length === 0 ? (
                <div className="p-10 text-center text-sm text-muted-foreground">
                  <SearchX className="mx-auto mb-2 h-6 w-6" />
                  <p>За фільтром нічого не знайдено</p>
                  {/* §0: живий CTA */}
                  <Button variant="outline" size="sm" className="mt-3" onClick={resetFilters}>
                    <RotateCcw className="mr-1 h-4 w-4" /> Скинути фільтр
                  </Button>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                      <th className="px-4 py-2.5 font-medium">Користувач</th>
                      <th className="px-2 py-2.5 font-medium">Рівень доступу</th>
                      <th className="px-2 py-2.5 font-medium">Функція / Відділ</th>
                      <th className="px-2 py-2.5 font-medium">Команда / Відділ</th>
                      <th className="px-2 py-2.5 font-medium">Активн.</th>
                      <th className="px-4 py-2.5 font-medium">Стан</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((u) => (
                      <tr
                        key={u.id}
                        onClick={() => setDrawerUserId(u.id)}
                        className={cn(
                          "cursor-pointer border-t transition-colors hover:bg-accent/50",
                          highlightId === u.id && "bg-primary-soft",
                        )}
                      >
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-2.5">
                            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-info text-[11px] text-info-foreground">
                              {initials(u.name)}
                            </span>
                            <span>
                              <span className="block leading-tight">{u.name}</span>
                              <span className="block text-xs leading-tight text-muted-foreground">
                                {u.email || u.userName}
                              </span>
                            </span>
                          </div>
                        </td>
                        <td className="px-2 py-2.5">
                          <TierBadge user={u} />
                        </td>
                        <td className="px-2 py-2.5">
                          {u.functional.length === 0 && u.capabilities.length === 0 ? (
                            <span className="text-muted-foreground">—</span>
                          ) : (
                            <span className="flex flex-wrap gap-1">
                              {u.functional.map((f) => (
                                <Badge key={f.key} variant="outline" className="rounded-badge text-xs">
                                  {f.label}
                                </Badge>
                              ))}
                              {u.capabilities.map((c) => (
                                <span
                                  key={c.key}
                                  className="rounded-badge bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground"
                                >
                                  {c.label}
                                </span>
                              ))}
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-2.5">
                          <TeamCell user={u} />
                        </td>
                        <td className="px-2 py-2.5 tabular-nums text-muted-foreground">
                          {formatDate(u.lastLoginAt)}
                        </td>
                        <td className="px-4 py-2.5">
                          <StateBadge active={u.isActive} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* P5/MP4: системні — read-only, ЛИШЕ реально наявні */}
              {showSystem && (
                <div className="border-t px-4 py-3">
                  <p className="mb-2 text-[11px] uppercase tracking-wide text-muted-foreground">
                    Системні · read-only · лише реально наявні
                  </p>
                  {listQ.data.systemUsers.map((s) => (
                    <div key={s.id} className="flex items-center gap-2.5 py-1.5 opacity-70">
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-muted">
                        <Bot className="h-3.5 w-3.5 text-muted-foreground" />
                      </span>
                      <span className="text-sm">{s.userName}</span>
                      <span className="text-xs text-muted-foreground">{s.note}</span>
                      <Badge variant="outline" className="ml-auto rounded-badge text-xs">
                        Системний
                      </Badge>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-1.5 border-t px-4 py-2.5 text-xs text-muted-foreground">
                <Lock className="h-3.5 w-3.5" />
                Рівень доступу — похідне з ролі (resolver), read-only тут · змінюється через
                «Змінити роль»
              </div>
            </>
          )}
        </div>
      </div>

      {/* Drawer */}
      {drawerUser && (
        <UserDrawer
          user={drawerUser}
          options={listQ.data!.options}
          allowedDomains={listQ.data!.allowedDomains}
          onClose={() => setDrawerUserId(null)}
          onToast={showToast}
        />
      )}

      {/* Wizard */}
      {listQ.data && (
        <AddUserWizard
          open={wizardOpen}
          options={listQ.data.options}
          onClose={() => {
            setWizardOpen(false);
            if (searchParams.get("new")) {
              searchParams.delete("new");
              setSearchParams(searchParams, { replace: true });
              navigate("/admin/users", { replace: true });
            }
          }}
          onCreated={(id, name) => {
            setWizardOpen(false);
            setHighlightId(id);
            showToast("ok", `Користувача ${name} створено`);
            window.setTimeout(() => setHighlightId(null), 5000);
          }}
        />
      )}

      {/* Toast */}
      {toast && (
        <div
          role="status"
          className={cn(
            "fixed bottom-4 right-4 z-50 rounded-lg px-4 py-2.5 text-sm shadow-lg",
            toast.kind === "ok"
              ? "bg-success text-success-foreground"
              : "bg-destructive-soft text-destructive",
          )}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
}

// ─── Drawer (canonical D-031) ────────────────────────────────────────────────

function UserDrawer({
  user,
  options,
  allowedDomains,
  onClose,
  onToast,
}: {
  user: AdminUserRow;
  options: NonNullable<ReturnType<typeof useAdminUsers>["data"]>["options"];
  allowedDomains: string[];
  onClose: () => void;
  onToast: (kind: "ok" | "err", msg: string) => void;
}) {
  const panelRef = useDrawerA11y(true, onClose);
  const [mode, setMode] = useState<"detail" | "roles" | "team" | "login">("detail");
  const [confirmDeact, setConfirmDeact] = useState(false);
  const [loginErr, setLoginErr] = useState<string | null>(null);

  const [roleCfg, setRoleCfg] = useState<RoleConfig>({
    tier:
      user.tier === "admin_data_all"
        ? "admin"
        : user.tier,
    functional: user.functional.length ? user.functional[0].key : null,
    capabilities: user.capabilities.map((c) => c.key),
    adminConfirmed: false,
  });
  const [teamSel, setTeamSel] = useState<string | null>(user.teams[0]?.id ?? null);

  const rolesM = useSetUserRoles();
  const teamM = useSetUserTeam();
  const activeM = useSetUserActive();
  const loginM = useSetUserLogin();

  const busy = rolesM.isPending || teamM.isPending || activeM.isPending || loginM.isPending;

  const loginAction = user.allowedActions?.find((a) => a.action === "login_edit");
  const canEditLogin = !!loginAction?.enabled;

  return (
    <div className="fixed inset-0 z-40">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Користувач ${user.name}`}
        className="absolute right-0 top-0 h-full w-[380px] overflow-y-auto border-l bg-card p-5 shadow-xl outline-none"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold">{user.name}</h2>
          <Button variant="ghost" size="sm" aria-label="Закрити" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {mode === "detail" && (
          <>
            {/* MP2 detail */}
            <dl className="text-sm">
              <Kv label="Рівень доступу">
                <TierBadge user={user} />
              </Kv>
              <Kv label="Функція / Відділ">
                {user.functional.length ? user.functional.map((f) => f.label).join(", ") : "—"}
              </Kv>
              <Kv label="Додаткові права">
                {user.capabilities.length ? user.capabilities.map((c) => c.label).join(", ") : "—"}
              </Kv>
              <Kv label="Команда / Відділ">
                <TeamCell user={user} />
              </Kv>
              <Kv label="Стан">
                <StateBadge active={user.isActive} />
              </Kv>
              <Kv label="Остання активність">{formatDate(user.lastLoginAt)}</Kv>
            </dl>

            {/* D-123 — «Вхід»: логін editable (guarded), email read-only (D-125) */}
            <div className="mt-4 border-t pt-3">
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Вхід
              </p>

              {/* Логін row */}
              <div className="py-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-xs text-muted-foreground">Логін (Ім'я користувача)</div>
                    <div className="break-all font-mono text-[13px]">{user.userName}</div>
                  </div>
                  {canEditLogin ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="shrink-0"
                      onClick={() => {
                        setLoginErr(null);
                        setMode("login");
                      }}
                    >
                      <Pencil className="mr-1.5 h-3.5 w-3.5" /> Змінити логін
                    </Button>
                  ) : (
                    <span className="inline-flex shrink-0 items-center gap-1 text-[11px] font-medium text-muted-foreground/80">
                      <Lock className="h-3 w-3" /> Недоступно
                    </span>
                  )}
                </div>
                {/* mismatch warning — backend loginMismatch (НЕ статичний текст) */}
                {user.loginMismatch && user.expectedGoogleEmail && (
                  <div className="mt-2 flex items-start gap-2 rounded-md border border-warning-foreground/20 bg-warning px-2.5 py-2 text-xs leading-relaxed text-warning-foreground">
                    <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />
                    <span>
                      Не збігається з <b>Google-поштою</b> {user.expectedGoogleEmail} → вхід через
                      Google неможливий. Виправте логін.
                    </span>
                  </div>
                )}
                {/* 0 мовчазних disabled: reasonCode → текст */}
                {!canEditLogin && (
                  <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">
                    {loginReasonText(loginAction?.reasonCode)}
                  </p>
                )}
              </div>

              {/* Email row — read-only (D-125) */}
              <div className="py-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-xs text-muted-foreground">Email у HUB</div>
                    <div className="break-all font-mono text-[13px]">{user.email || "—"}</div>
                  </div>
                  <span className="inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold text-muted-foreground/80">
                    <Lock className="h-3 w-3" /> read-only
                  </span>
                </div>
                <p className="mt-1.5 flex items-start gap-1.5 text-[11px] leading-relaxed text-muted-foreground">
                  <Info className="mt-px h-3 w-3 shrink-0" />
                  <span>Ключ прив'язки клієнтів. Змінюється окремою процедурою — рішення D-125 у роботі.</span>
                </p>
              </div>
            </div>

            {/* D-048: BI-синхронізація — лише для рівня «Менеджер» (MP4) */}
            {user.tier === "manager" && (
              <ManagerSyncSection userId={user.id} onToast={onToast} />
            )}

            <div className="mt-5 flex flex-col gap-2">
              {/* MP3: canonical role-assignment */}
              <Button variant="outline" size="sm" onClick={() => setMode("roles")}>
                <Shield className="mr-1.5 h-4 w-4" /> Змінити роль
              </Button>
              <Button variant="outline" size="sm" onClick={() => setMode("team")}>
                <Users className="mr-1.5 h-4 w-4" /> Команда / Відділ
              </Button>
              {user.isActive ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive"
                  onClick={() => setConfirmDeact(true)}
                >
                  <UserX className="mr-1.5 h-4 w-4" /> Деактивувати
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() =>
                    activeM.mutate(
                      { userId: user.id, active: true },
                      {
                        onSuccess: () => onToast("ok", `${user.name} реактивовано`),
                        onError: (e) => onToast("err", e instanceof Error ? e.message : "Помилка"),
                      },
                    )
                  }
                >
                  <CheckCircle2 className="mr-1.5 h-4 w-4" /> Реактивувати
                </Button>
              )}
            </div>
          </>
        )}

        {mode === "roles" && (
          <>
            <RoleAssignmentFields
              value={roleCfg}
              onChange={setRoleCfg}
              options={options}
              sections={["tier", "functional", "capabilities"]}
              tierLocked={user.type === "admin"}
            />
            <DrawerActions
              busy={rolesM.isPending}
              onCancel={() => setMode("detail")}
              onSave={() =>
                rolesM.mutate(
                  { userId: user.id, cfg: roleCfg },
                  {
                    onSuccess: () => {
                      onToast("ok", "Ролі оновлено");
                      setMode("detail");
                    },
                    onError: (e) => onToast("err", e instanceof Error ? e.message : "Помилка"),
                  },
                )
              }
              saveDisabledReason={
                user.type !== "admin" && !roleCfg.tier
                  ? "Оберіть Рівень доступу (NULL=DENY)"
                  : null
              }
            />
          </>
        )}

        {mode === "team" && (
          <>
            <div className="mb-2 text-xs text-muted-foreground">Команда (1:1 відділ, A-lite)</div>
            <div className="flex flex-wrap gap-1.5">
              {options.teams.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  aria-pressed={teamSel === t.id}
                  onClick={() => setTeamSel(teamSel === t.id ? null : t.id)}
                  className={cn(
                    "rounded-badge px-2.5 py-1 text-xs",
                    teamSel === t.id
                      ? "bg-info text-info-foreground"
                      : "bg-muted text-muted-foreground hover:bg-accent hover:text-foreground",
                  )}
                >
                  {t.name}
                  {t.departmentName ? ` → ${t.departmentName}` : ""}
                </button>
              ))}
            </div>
            {!teamSel && (
              <p className="mt-2 rounded bg-warning px-2 py-1.5 text-xs text-warning-foreground">
                Без команди: team-scope недоступний (warning у списку).
              </p>
            )}
            <DrawerActions
              busy={teamM.isPending}
              onCancel={() => setMode("detail")}
              onSave={() =>
                teamM.mutate(
                  { userId: user.id, teamId: teamSel },
                  {
                    onSuccess: () => {
                      onToast("ok", "Команду оновлено");
                      setMode("detail");
                    },
                    onError: (e) => onToast("err", e instanceof Error ? e.message : "Помилка"),
                  },
                )
              }
              saveDisabledReason={null}
            />
          </>
        )}

        {mode === "login" && (
          <LoginEditForm
            user={user}
            allowedDomains={allowedDomains}
            busy={loginM.isPending}
            serverError={loginErr}
            onCancel={() => {
              setLoginErr(null);
              setMode("detail");
            }}
            onConfirm={(value) => {
              setLoginErr(null);
              loginM.mutate(
                { userId: user.id, userName: value },
                {
                  onSuccess: (res) => {
                    if (res.warning) {
                      // §6: revoke-fail → НЕ тихий success
                      onToast("err", res.warning);
                    } else {
                      onToast("ok", "Логін змінено");
                    }
                    // post-write refresh (D-30): картка перемалюється з backend
                    setMode("detail");
                  },
                  onError: (e) => setLoginErr(e instanceof Error ? e.message : "Помилка"),
                },
              );
            }}
          />
        )}

        {/* MP5: confirm-dialog (locked) */}
        {confirmDeact && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-5">
            <div role="alertdialog" aria-modal="true" className="w-full max-w-sm rounded-card border bg-card p-4 shadow-xl">
              <div className="mb-2 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-warning-foreground" />
                <b className="text-sm">Деактивувати {user.name}?</b>
              </div>
              <p className="mb-4 text-sm text-muted-foreground">
                Користувач втратить доступ, дані збережуться. Дію записано в аудит.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setConfirmDeact(false)}>
                  Скасувати
                </Button>
                <Button
                  size="sm"
                  disabled={busy}
                  onClick={() =>
                    activeM.mutate(
                      { userId: user.id, active: false },
                      {
                        onSuccess: () => {
                          onToast("ok", `${user.name} деактивовано`);
                          setConfirmDeact(false);
                          onClose();
                        },
                        onError: (e) => {
                          onToast("err", e instanceof Error ? e.message : "Помилка");
                          setConfirmDeact(false);
                        },
                      },
                    )
                  }
                >
                  {activeM.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                  Підтвердити
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DrawerActions({
  busy,
  onCancel,
  onSave,
  saveDisabledReason,
}: {
  busy: boolean;
  onCancel: () => void;
  onSave: () => void;
  saveDisabledReason: string | null;
}) {
  return (
    <div className="mt-4 border-t pt-3.5">
      {saveDisabledReason && (
        <p className="mb-2 rounded bg-destructive-soft px-2 py-1.5 text-xs text-destructive">
          {saveDisabledReason}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onCancel}>
          Скасувати
        </Button>
        <Button size="sm" disabled={busy || !!saveDisabledReason} onClick={onSave}>
          {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
          Зберегти
        </Button>
      </div>
    </div>
  );
}

// ─── Login edit (D-123) ─────────────────────────────────────────────────────

/** reasonCode → user-facing текст (0 мовчазних disabled, TZ §2). */
function loginReasonText(code?: AdminUserAllowedAction["reasonCode"]): string {
  switch (code) {
    case "self_edit_blocked":
      return "Власний вхід редагувати не можна — попросіть іншого адміністратора.";
    case "last_admin_protection":
      return "Не можна змінювати логін останнього активного адміністратора.";
    case "no_capability":
      return "Недостатньо прав для зміни логіну.";
    default:
      return "Зміна логіну недоступна.";
  }
}

/** Клієнтська валідація = дзеркало backend (SoT). Повертає UA-помилку або null. */
function validateLogin(raw: string, allowedDomains: string[], currentUserName: string): string | null {
  const v = raw.trim().toLowerCase();
  if (!v) return "Вкажіть новий логін.";
  if (/\s/.test(v)) return "Логін не може містити пробіли.";
  // eslint-disable-next-line no-control-regex
  if (!/^[\x21-\x7e]+$/.test(v)) return "Логін містить недопустимі символи.";
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) return "Логін має бути e-mail (name@kormaxtrade.com.ua).";
  const domain = v.split("@")[1];
  if (!allowedDomains.includes(domain))
    return "Дозволені лише корпоративні домени Kormax. Google SSO інші не пропустить.";
  if (v === currentUserName.trim().toLowerCase()) return "Новий логін збігається з поточним.";
  return null;
}

function LoginEditForm({
  user,
  allowedDomains,
  busy,
  serverError,
  onCancel,
  onConfirm,
}: {
  user: AdminUserRow;
  allowedDomains: string[];
  busy: boolean;
  serverError: string | null;
  onCancel: () => void;
  onConfirm: (value: string) => void;
}) {
  const [value, setValue] = useState(user.expectedGoogleEmail ?? "");
  const [step, setStep] = useState<"form" | "confirm">("form");
  const [clientErr, setClientErr] = useState<string | null>(null);

  // Backend-помилка (409/домен/тощо) → повернути на форму для виправлення.
  useEffect(() => {
    if (serverError) setStep("form");
  }, [serverError]);

  const normalized = value.trim().toLowerCase();
  const willNormalize = value.trim() !== "" && value.trim() !== normalized;
  const err = clientErr ?? serverError;

  const proceed = () => {
    const v = validateLogin(value, allowedDomains, user.userName);
    setClientErr(v);
    if (!v) setStep("confirm");
  };

  if (step === "confirm") {
    return (
      <div className="pt-1">
        <div className="mb-1 text-[15px] font-semibold">Змінити логін {user.name}?</div>
        <div className="text-sm leading-relaxed text-muted-foreground">
          <span className="font-mono">{user.userName}</span> →{" "}
          <b className="break-all font-mono text-foreground">{normalized}</b>
          <br />
          Клієнти (<b className="text-foreground">{user.managedClientCount}</b>) та email не зміняться.
        </div>
        <div className="mt-3 flex items-start gap-2 rounded-md bg-muted px-2.5 py-2 text-xs leading-relaxed text-muted-foreground">
          <Info className="mt-px h-3.5 w-3.5 shrink-0" />
          <span>
            Активні сесії користувача буде завершено — він увійде знову через Google. Дію буде
            записано в журнал безпеки.
          </span>
        </div>
        {err && (
          <p className="mt-2 flex items-start gap-1.5 text-xs font-medium text-destructive">
            <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" /> {err}
          </p>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" size="sm" disabled={busy} onClick={() => setStep("form")}>
            Назад
          </Button>
          <Button size="sm" disabled={busy} onClick={() => onConfirm(normalized)}>
            {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            Змінити логін
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-1">
      <div className="mb-1 flex items-center gap-1.5 text-sm font-semibold">
        <KeyRound className="h-4 w-4" /> Змінити логін
      </div>
      <p className="mb-3 font-mono text-xs text-muted-foreground">
        {user.name} · поточний логін: {user.userName}
      </p>
      <label className="mb-1.5 block text-xs font-medium text-muted-foreground" htmlFor="new-login">
        Новий логін
      </label>
      <Input
        id="new-login"
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          setClientErr(null);
        }}
        autoComplete="off"
        spellCheck={false}
        className={cn("font-mono text-sm", err && "border-destructive")}
        aria-invalid={!!err}
      />
      {err ? (
        <p className="mt-2 flex items-start gap-1.5 text-xs font-medium text-destructive">
          <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" /> {err}
        </p>
      ) : willNormalize ? (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-success-foreground">
          <CheckCircle2 className="h-3.5 w-3.5" /> Збережеться малими літерами: «{normalized}»
        </p>
      ) : (
        <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">
          Логін = робоча Google-пошта корпоративного домену. Власник клієнтів (з BI) не зміниться.
        </p>
      )}
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onCancel}>
          Скасувати
        </Button>
        <Button size="sm" onClick={proceed}>
          Продовжити
        </Button>
      </div>
    </div>
  );
}

// ─── Cell helpers ────────────────────────────────────────────────────────────

function TierBadge({ user }: { user: AdminUserRow }) {
  if (user.tier === null) {
    return (
      <span className="inline-flex items-center gap-1 rounded-badge bg-warning px-2 py-0.5 text-xs text-warning-foreground">
        <AlertTriangle className="h-3 w-3" />
        {user.tierConflict ? "Конфлікт ролей" : "Без ролі"}
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 rounded-badge bg-info px-2 py-0.5 text-xs text-info-foreground"
      title="Похідне з ролі · Tier"
    >
      <Lock className="h-3 w-3" />
      {user.tierLabel}
    </span>
  );
}

function TeamCell({ user }: { user: AdminUserRow }) {
  if (user.teams.length === 0) {
    return (
      <span className="inline-flex items-center gap-1 rounded-badge bg-warning px-2 py-0.5 text-xs text-warning-foreground">
        <AlertTriangle className="h-3 w-3" /> Без команди
      </span>
    );
  }
  return (
    <span className="text-sm">
      {user.teams
        .map((t) => (t.departmentName ? `${t.name} → ${t.departmentName}` : t.name))
        .join(", ")}
    </span>
  );
}

function StateBadge({ active }: { active: boolean }) {
  return active ? (
    <span className="inline-flex items-center gap-1 rounded-badge bg-success px-2 py-0.5 text-xs text-success-foreground">
      <CheckCircle2 className="h-3 w-3" /> Активний
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-badge bg-muted px-2 py-0.5 text-xs text-muted-foreground">
      <CircleSlash className="h-3 w-3" /> Деактивований
    </span>
  );
}

function Kv({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b py-2">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-right">{children}</dd>
    </div>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso.replace(" ", "T") + "Z");
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("uk-UA", { day: "2-digit", month: "2-digit", year: "numeric" });
}
